import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Controller, useForm, useWatch } from "react-hook-form";
import { CreateModuleInstanceDto } from "../../../model/module/dto/module-instance/CreateModuleInstanceDto";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { getRenderedFieldByType } from "../../entries/utils/getRenderedFieldByType";
import { Flex } from "../../../components/Flex";
import { AtiraToast } from "../../../utils/AtiraToast";
import { isCustomDateInput } from "../../../utils/isCustomDateInput";
import { onSetTypedCRUDErrors } from "../../../utils/onSetTypedCRUDErrors";
import { moduleActions } from "../../../redux/module/module.slice";
import { ModuleDefinition } from "../../../model/module/types/module-definition/ModuleDefinition";
import isEmpty from "lodash/isEmpty";
import { Info } from "../../../components/Info";
import { Lengths } from "../../../model/shared/enum/Lengths.enum";
import { Entry } from "../../../model/entry/Entry";
import { Drawer } from "../../../components/Drawer";
import { PageMeta } from "../../../model/shared/meta/PageMeta";
import { DrawerDefaultsExtra } from "../../tasks/components/DrawerDefaultsExtra";
import { InputTypes } from "../../../model/form/types/InputTypes.enum";
import { EntriesSearchDropdown } from "../../../components/shared/EntriesSearchDropdown";
import { getEntryLabel } from "../../../utils/entryUtil";
import styled from "styled-components";
import { Button } from "../../../components/Button";
import { Spacing } from "../../../theme/Spacing";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { CreateEntryDto } from "../../../model/entry/dto/CreateNewEntryDto";
import { useDrawersContext } from "../../tasks/drawers-context";
import { formSliceSelectors } from "../../../redux/forms/form.selector";
import { Text } from "../../../components/Text";
import { isValidDate, standardDate, parseDateKey } from "../../../utils/Date";

const NewEntryButton = styled(Button)`
  padding: ${Spacing.s} ${Spacing.m};
  margin: 0;
  background-color: transparent;
  color: ${({ theme }) => theme.main};
  height: 2.3rem;
  flex-shrink: 0;
  border: ${({ theme }) => `1px solid ${theme.main}`};

  &:hover {
    background-color: ${({ theme }) => theme.main};
    color: ${({ theme }) => theme.white};
  }
`;

const StyledList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
`;

type DynamicModuleInstanceCreateDrawerProps = {
  open: boolean;
  onClose: VoidFunction;
  moduleDefinition?: ModuleDefinition | null;
  entry?: Entry | null;
};

export const DynamicModuleInstanceCreateDrawer: React.FC<
  DynamicModuleInstanceCreateDrawerProps
> = ({ open = false, onClose, moduleDefinition, entry }) => {
  const [loading, setLoading] = useState(false);
  const [createEntryDto, setCreateEntryDto] = useState<CreateEntryDto | null>(
    null,
  );

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { control, setError, reset, getValues, handleSubmit, setValue } =
    useForm<CreateModuleInstanceDto>();
  const { setEntryCreateVisible, setEntryCreateAction } = useDrawersContext();

  const user = useAppSelector(userSliceSelectors.selectLoggedInUser);
  const forms = useAppSelector(formSliceSelectors.selectForms);

  const createEntryForm = forms?.find((f) => f._id === createEntryDto?.formId);
  const createEntryFormTitle = createEntryForm?.orphan
    ? t("forms.manual_entry")
    : createEntryForm?.title;
  const isThereCreateEntryDto = !isEmpty(createEntryDto);
  const canCreateEntry = user?.permissions?.permissions?.ENTRY.CREATE === true;

  const [entryId, fields] = useWatch({
    control,
    name: ["entryId", "fields"],
  });

  const selectedEntryId = entryId || entry?._id;

  const requiredInputs = useMemo(() => {
    return moduleDefinition?.inputs?.filter(
      (input) => input?.settings?.required === true,
    );
  }, [moduleDefinition?.inputs]);

  const isDisabled = useMemo(() => {
    if (!isEmpty(createEntryDto)) {
      return false;
    }

    if (!selectedEntryId) {
      return true;
    }

    return requiredInputs?.some((input) => {
      if (input.type.toLowerCase() === InputTypes.DATE.toLowerCase()) {
        return !fields?.[input.name];
      }

      if (input.type.toLowerCase() === InputTypes.NUMBER.toLowerCase()) {
        return isNaN(Number(fields?.[input.name]));
      }

      return isEmpty(fields?.[input.name]);
    });
  }, [createEntryDto, fields, requiredInputs, selectedEntryId]);

  useEffect(() => {
    if (open) {
      dispatch(moduleActions.getAllModuleDefinitions({ userId: user?._id! }));
    }
  }, [open, dispatch, user?._id]);

  const renderedInputs = useMemo(() => {
    return moduleDefinition?.inputs?.map((input) => {
      return (
        <Controller
          key={input._id}
          name={`fields.${input.name}`}
          control={control}
          rules={{
            required: !!input?.settings?.required,
            maxLength: {
              value: input?.settings?.maxChar || Lengths.DESCRIPTION,
              message: t("error.length.less", {
                length: input?.settings?.maxChar || Lengths.DESCRIPTION,
              }),
            },
          }}
          render={({ field, fieldState }) =>
            getRenderedFieldByType(
              input,
              field,
              fieldState,
              input.label,
              undefined,
              {
                // AtiraDatePickerFlat indexes possibleBookedSlots by input
                // name itself - pass the full record (not already indexed
                // by input.name), matching FixedModuleInstanceCreateDrawer.
                possibleBookedSlots: moduleDefinition?._possibleBookedSlots,
              },
            )
          }
        />
      );
    });
  }, [
    moduleDefinition?.inputs,
    moduleDefinition?._possibleBookedSlots,
    control,
    t,
  ]);

  const localOnClose = () => {
    onClose();
    reset();
    setCreateEntryDto(null);
  };

  const onCreateNewEntry = () => {
    setEntryCreateVisible(true);

    setEntryCreateAction(() => (dto: CreateEntryDto) => {
      setCreateEntryDto(dto);
      setEntryCreateVisible(false);
    });
  };

  const onSave = async () => {
    try {
      setLoading(true);

      const dto = getValues();

      const normalizedFields = parseDateKey(dto.fields || {}, (name) => {
        const parentInput = moduleDefinition?.inputs?.find(
          (i) => i.name === name,
        );

        if (!parentInput) {
          return false;
        }

        return isCustomDateInput(parentInput.type, parentInput.dateInputConfig);
      });

      const payload = {
        fields: normalizedFields,
        moduleDefinitionId: moduleDefinition?._id,
        entryId: isEmpty(entry) ? dto.entryId : entry?._id,
        ...(isThereCreateEntryDto ? { entryDto: createEntryDto } : {}),
        userId: user?._id!,
      } as CreateModuleInstanceDto;

      await dispatch(moduleActions.createModuleInstance(payload)).unwrap();

      await dispatch(
        moduleActions.getModuleInstances({
          userId: user?._id!,
          moduleDefinitionId: moduleDefinition?._id!,
          keyword: "",
          meta: PageMeta.create({
            page: 0,
            count: 1000,
          }),
        }),
      ).unwrap();

      localOnClose();

      AtiraToast.success(t("modules.instance.create.success"));
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setLoading(false);
    }
  };

  if (isEmpty(moduleDefinition)) {
    return null;
  }

  return (
    <Drawer
      open={open}
      onClose={localOnClose}
      title={t("modules.instance.create.title", {
        moduleDefinition: moduleDefinition.name,
      })}
      maskClosable={false}
      extra={
        <DrawerDefaultsExtra
          onSubmit={handleSubmit(onSave)}
          onCancel={onClose}
          config={{
            loading,
            disabled: isDisabled,
          }}
        />
      }
      size="large"
      key={`${moduleDefinition._id}-${entry?._id}`}
    >
      <Flex width={"100%"} flexDirection="column" gap="m">
        {!isEmpty(entry) ? (
          <Info
            info={
              <Trans
                i18nKey={"modules.instance.create.info"}
                values={{ entry: getEntryLabel(entry!) }}
                components={[<strong />]}
              />
            }
          />
        ) : (
          <Fragment>
            {canCreateEntry ? (
              <Flex
                gap="s"
                width={"100%"}
                alignItems="center"
                flexDirection="column"
              >
                <EntriesSearchDropdown
                  setValue={setValue}
                  required
                  isSearchVisible={open}
                />

                <NewEntryButton icon={faPlus} onClick={onCreateNewEntry}>
                  {t("modules.instance.update.createEntry.button")}
                </NewEntryButton>
              </Flex>
            ) : null}
          </Fragment>
        )}

        {isThereCreateEntryDto ? (
          <Info
            info={
              <Flex flexDirection="column" gap="s">
                <Text color="main">
                  <Trans
                    i18nKey={"modules.instance.update.createEntry.info"}
                    values={{ formTitle: createEntryFormTitle }}
                    components={[<strong />]}
                  />
                </Text>

                <StyledList>
                  {Object.entries(createEntryDto)
                    .filter(
                      ([key, value]) =>
                        !isEmpty(value) &&
                        !["formId", "ownerId", "origin"].includes(key),
                    )
                    .map(([key, value]) => {
                      const fieldLabel = !createEntryForm?.orphan
                        ? createEntryForm?.inputs?.find(
                            (input) => input.name === key,
                          )?.label
                        : t(`common.${key}`);

                      return (
                        <li key={key}>
                          <Text color="main">
                            <strong>{fieldLabel}: </strong>
                            <span>
                              {isValidDate(value)
                                ? standardDate<string>(value as Date, user)
                                : (value as string)}
                            </span>
                          </Text>
                        </li>
                      );
                    })}
                </StyledList>
              </Flex>
            }
          />
        ) : null}

        {renderedInputs}
      </Flex>
    </Drawer>
  );
};
